import dayjs from 'dayjs'
import * as React from 'react'

export interface Serializer<S> {
  read: (str: string) => S
  write: (value: S) => string
}

function createDefaultSerializer<S>(): Serializer<S> {
  return {
    read: (json) => JSON.parse(json),
    write: (value) => JSON.stringify(value),
  }
}

export const dateSerializer: Serializer<dayjs.Dayjs | undefined> = {
  read: (str: string) => (str ? dayjs(str) : undefined),
  write: (value: dayjs.Dayjs | undefined) => (value ? value.toISOString() : ''),
}

export const useLocalStorage = <S>(
  key: string,
  initialValue: S,
  customSerializer?: Serializer<S>
): [S, (value: S) => void] => {
  const serializer = customSerializer || createDefaultSerializer<S>()
  const [storedValue, setStoredValue] = React.useState<S>(() => {
    try {
      const str = window.localStorage.getItem(key)
      return str ? serializer.read(str) : initialValue
    } catch (error) {
      return initialValue
    }
  })

  React.useEffect(() => {
    const callback: Callback = (nextValue) => setStoredValue(nextValue)
    addCallback(key, callback)
    return () => removeCallback(key, callback)
  })

  const setValue = (value: S) => {
    setStoredValue(value)
    forEachCallback(key, (cb) => cb(value))
    window.localStorage.setItem(key, serializer.write(value))
  }

  return [storedValue, setValue]
}

type Callback = (value: any) => void

const callbacks = new Map<string, Callback[]>()

function addCallback(key: string, callback: Callback): void {
  callbacks.set(key, (callbacks.get(key) || []).concat([callback]))
}
function removeCallback(key: string, callback: Callback): void {
  callbacks.set(
    key,
    (callbacks.get(key) || []).filter((cb) => cb !== callback)
  )
}
function forEachCallback(key: string, fn: (callback: Callback) => void): void {
  const cbs = callbacks.get(key) || []
  cbs.forEach(fn)
}
